import React, { useEffect, useState } from "react";
import { Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { SettingsV2RayTariffsComponent } from "../payment/settings-v2ray-tariffs.component";
import { SettingsCardNumbersComponent } from "../payment/settings-card-numbers.component";
import { VmessCategoryListComponent } from "../../vmess-categories/list";
import { VmessAccountsListComponent } from "../../vmess-accounts/list";
import { SshCategoryListComponent } from "../../ssh-categories/list";
import { SshAccountsListComponent } from "../../ssh-accounts/list";

type VpnSettingsTab = "v2ray" | "vmess" | "ssh" | "card-numbers";
type InventorySubTab = "accounts" | "tariffs";

const TAB_CONFIG: { id: VpnSettingsTab; label: string; description: string }[] = [
  {
    id: "v2ray",
    label: "V2Ray",
    description: "تعرفه و قیمت‌گذاری اشتراک‌های V2Ray",
  },
  {
    id: "vmess",
    label: "VMess",
    description: "مدیریت حساب‌ها و تعرفه‌های VMess",
  },
  {
    id: "ssh",
    label: "SSH",
    description: "مدیریت حساب‌ها و تعرفه‌های SSH",
  },
  {
    id: "card-numbers",
    label: "شماره کارت",
    description: "اطلاعات کارت بانکی و تنظیمات پرداخت",
  },
];

const LEGACY_TAB_MAP: Record<string, VpnSettingsTab> = {
  marzban: "v2ray",
  categories: "vmess",
  accounts: "vmess",
};

export const VpnSettingsComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const subtabParam = searchParams.get("subtab") as InventorySubTab | null;

  const resolveTab = (): VpnSettingsTab => {
    if (!tabParam) return "v2ray";
    if (LEGACY_TAB_MAP[tabParam]) return LEGACY_TAB_MAP[tabParam];
    if (TAB_CONFIG.some((t) => t.id === tabParam)) {
      return tabParam as VpnSettingsTab;
    }
    return "v2ray";
  };

  const [activeTab, setActiveTab] = useState<VpnSettingsTab>(resolveTab());
  const [inventorySubTab, setInventorySubTab] = useState<InventorySubTab>(
    subtabParam === "tariffs" ? "tariffs" : "accounts"
  );

  useEffect(() => {
    const tab = resolveTab();
    setActiveTab(tab);
    if (tab === "vmess" || tab === "ssh") {
      setInventorySubTab(subtabParam === "tariffs" ? "tariffs" : "accounts");
    }
  }, [tabParam, subtabParam]);

  const handleTabChange = (_: React.SyntheticEvent, value: VpnSettingsTab) => {
    setActiveTab(value);
    if (value === "vmess" || value === "ssh") {
      setSearchParams({ tab: value, subtab: inventorySubTab });
    } else {
      setSearchParams({ tab: value });
    }
  };

  const handleInventorySubTabChange = (
    _: React.SyntheticEvent,
    value: InventorySubTab
  ) => {
    setInventorySubTab(value);
    if (activeTab === "vmess" || activeTab === "ssh") {
      setSearchParams({ tab: activeTab, subtab: value });
    }
  };

  const activeConfig = TAB_CONFIG.find((t) => t.id === activeTab)!;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        تنظیمات VPN
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        مدیریت یکپارچه انواع VPN: V2Ray، VMess و SSH
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
        {activeConfig.description}
      </Typography>

      {activeTab === "v2ray" && <SettingsV2RayTariffsComponent embedded />}
      {activeTab === "card-numbers" && <SettingsCardNumbersComponent embedded />}

      {activeTab === "vmess" && (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={inventorySubTab}
              onChange={handleInventorySubTabChange}
            >
              <Tab value="accounts" label="حساب‌ها" />
              <Tab value="tariffs" label="تعرفه‌ها" />
            </Tabs>
          </Paper>
          {inventorySubTab === "accounts" && (
            <VmessAccountsListComponent embedded />
          )}
          {inventorySubTab === "tariffs" && (
            <VmessCategoryListComponent embedded />
          )}
        </>
      )}

      {activeTab === "ssh" && (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={inventorySubTab}
              onChange={handleInventorySubTabChange}
            >
              <Tab value="accounts" label="حساب‌ها" />
              <Tab value="tariffs" label="تعرفه‌ها" />
            </Tabs>
          </Paper>
          {inventorySubTab === "accounts" && (
            <SshAccountsListComponent embedded />
          )}
          {inventorySubTab === "tariffs" && (
            <SshCategoryListComponent embedded />
          )}
        </>
      )}
    </Box>
  );
};
